import clsx from "clsx";
import { useMemo } from "react";

import { RenderWhen } from "@js2/components";
import { Button } from "@components/button";
import LoadingOverlay from "@components/LoadingOverlay";
import ModalWrapper from "@components/ModalWrapper";
import useGetExpandedDetailsLocal from "@modules/samples/hooks/useGetExpandedDetailsLocal";
import { useMwpDetectionsData } from "@modules/samples_cloud/hooks";
import CompareSamplesBody from "./CompareSamplesBody";

import styles from "./compare-samples.module.scss";

interface CompareSamplesModalProps {
  mainSha1: string;
  mainSourceId?: number;
  mainRelationshipId?: number;
  comparedSha1: string;
  // TLSH/SSDeep rows have none; the backend then takes the first upload, which
  // may not be RHA's.
  comparedSourceId?: number;
  onHide: () => void;
}

const CompareSamplesModal = ({
  mainSha1,
  mainSourceId,
  mainRelationshipId,
  comparedSha1,
  comparedSourceId,
  onHide
}: CompareSamplesModalProps): JSX.Element => {
  const main = useGetExpandedDetailsLocal(
    mainSha1,
    mainSourceId,
    mainRelationshipId
  );
  const compared = useGetExpandedDetailsLocal(comparedSha1, comparedSourceId);
  const mainDetections = useMwpDetectionsData(mainSha1);
  const comparedDetections = useMwpDetectionsData(comparedSha1);
  const detections = useMemo(
    () => ({ main: mainDetections.data, other: comparedDetections.data }),
    [mainDetections.data, comparedDetections.data]
  );

  const isLoading =
    main.isLoading ||
    compared.isLoading ||
    mainDetections.isLoading ||
    comparedDetections.isLoading;

  return (
    <ModalWrapper
      show
      // ModalWrapper disables it, leaving focus on the opener, so Escape never
      // reached the dialog.
      autoFocus
      onHide={onHide}
      size="xl"
      title="Compare samples"
      className="compare-samples"
      data-testid="compare-samples-modal"
      footer={
        <Button
          variant="secondary"
          data-testid="compare-samples-close"
          onClick={onHide}
        >
          Close
        </Button>
      }
    >
      <RenderWhen condition={isLoading}>
        <div className={styles.placeholder}>
          <LoadingOverlay />
        </div>
      </RenderWhen>

      <RenderWhen condition={!isLoading && !(main.data && compared.data)}>
        <div className={clsx(styles.placeholder, "text-center")}>
          Sample details could not be loaded, so these samples cannot be
          compared.
        </div>
      </RenderWhen>

      {main.data && compared.data && (
        <CompareSamplesBody
          main={main.data}
          compared={compared.data}
          detections={detections}
        />
      )}
    </ModalWrapper>
  );
};

export default CompareSamplesModal;
